import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

/**
 * Returns an invalidate for `queryKey` that runs at most once per window: the
 * first call refetches at once, calls inside the window collapse into one
 * trailing refetch at its end, so the last change is never dropped. For
 * expensive reads (the CRM KPIs) that a busy campaign's live events would
 * otherwise refetch on every event.
 *
 * `getIntervalMs` is read on each call, so the window can follow what the
 * server last said about how long its answer stays fresh. Both arguments must
 * be stable across renders (memoise them), or every render resets the throttle.
 */
export function useThrottledInvalidate(queryKey: QueryKey, getIntervalMs: () => number): () => void {
  const queryClient = useQueryClient();
  const lastRunAt = useRef<number | null>(null);
  const trailing = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (trailing.current) clearTimeout(trailing.current);
      trailing.current = null;
      // A new key (another campaign) starts its own window: its first event refreshes at once.
      lastRunAt.current = null;
    },
    [queryKey],
  );

  return useCallback(() => {
    if (trailing.current) return;
    const run = () => {
      trailing.current = null;
      lastRunAt.current = Date.now();
      void queryClient.invalidateQueries({ queryKey });
    };
    const wait = lastRunAt.current === null ? 0 : lastRunAt.current + getIntervalMs() - Date.now();
    if (wait <= 0) {
      run();
      return;
    }
    trailing.current = setTimeout(run, wait);
  }, [queryClient, queryKey, getIntervalMs]);
}
